import React, { useState, useEffect, useRef } from 'react';
import { PourConfig, CalculationResult, Language } from './types';
import { translations, LANGUAGE_NAMES } from './utils/translations';
import { calculateVolume } from './utils/calculations';
import CalculatorInput from './components/CalculatorInput';
import ResultsDisplay from './components/ResultsDisplay';
import ExpertAdvisor from './components/ExpertAdvisor';
import GlobalFooter from './components/GlobalFooter';
import { Hexagon, Globe, ChevronDown } from 'lucide-react';

const DEFAULT_CONFIG: PourConfig = {
  shape: 'rectangle',
  unitSystem: 'imperial',
  dimensions: {
    length: 0,
    width: 0,
    depth: 0,
    diameter: 0
  },
  ratioA: 1,
  ratioB: 1,
  wasteMargin: 5,
  pricePerUnit: 0
};

const DEFAULT_RESULT: CalculationResult = {
  totalVolume: 0,
  partA: 0,
  partB: 0,
  unit: 'fl oz',
  estimatedCost: 0
};

const App: React.FC = () => {
  const [config, setConfig] = useState<PourConfig>(DEFAULT_CONFIG);
  const [result, setResult] = useState<CalculationResult>(DEFAULT_RESULT);
  const [language, setLanguage] = useState<Language>('en');
  
  // Language Menu State
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);

  // Get current translations based on selected language
  const t = translations[language];

  useEffect(() => {
    const newResult = calculateVolume(config);
    setResult(newResult);
  }, [config]);

  // Handle clicks outside the language menu to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    }
    
    if (isLangMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLangMenuOpen]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900 flex flex-col">
      
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
              <Hexagon className="w-6 h-6 text-white fill-indigo-500/20" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">ResinCalc<span className="text-indigo-600">Pro</span></h1>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <div className="relative" ref={langMenuRef}>
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors bg-slate-50 hover:bg-slate-100 rounded-lg ${isLangMenuOpen ? 'text-indigo-600 bg-slate-100' : 'text-slate-600'}`}
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{LANGUAGE_NAMES[language]}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50">
                  {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                    <button
                      key={code}
                      onClick={() => {
                        setLanguage(code as Language);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700 transition-colors ${language === code ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-600'}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden md:block">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {t.nav.latest}
              </span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex-grow">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Input Form */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-2">
              <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight">{t.hero.title}</h2>
              <p className="text-lg text-slate-500">{t.hero.desc}</p>
            </div>
            
            <CalculatorInput config={config} onChange={setConfig} t={t} />

            {/* AI Advisor - Placed here on mobile, below inputs */}
            <div className="lg:hidden space-y-8">
               <ResultsDisplay result={result} t={t} />
               <ExpertAdvisor config={config} result={result} language={language} t={t} />
            </div>
          </div>

          {/* Right Column: Results & AI (Sticky on Desktop) */}
          <div className="hidden lg:block lg:col-span-5 space-y-8 sticky top-24">
            <ResultsDisplay result={result} t={t} />
            <ExpertAdvisor config={config} result={result} language={language} t={t} />
          </div>
        </div>
      </main>

      {/* App Specific Footer */}
      <footer className="bg-white border-t border-slate-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                 <Hexagon className="w-5 h-5 text-indigo-600" />
                 <span className="font-bold text-slate-900">ResinCalc Pro</span>
              </div>
              <p className="text-slate-500 text-sm max-w-sm">
                {t.footer.desc}
              </p>
            </div>
            <div className="text-left md:text-right space-y-2">
               <a href="mailto:service@promptvault.work" className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors block">
                 service@promptvault.work
               </a>
               <p className="text-xs text-slate-400">
                 {t.footer.disclaimer}
               </p>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Global Network Footer */}
      <GlobalFooter />
    </div>
  );
};

export default App;